/**
 * Connections · MCP servers (`/services/mcp`): the customer-hosted Model
 * Context Protocol servers whose tools the Teams may call. One row per
 * registered server; the server opens in the sheet with its tools (risk,
 * override, agent access, enabled) and the reader's own credential, and the
 * footer refreshes its manifest. Add MCP server opens the registration form
 * in the sheet.
 */
import { useCallback, useEffect, useRef, useState, type CSSProperties } from "react";
import { Btn, Group, Groups, MenuItem, RecordHead, Row, SheetFoot } from "../queue/index.js";
import { useOwnedSheet } from "../shell/useOwnedSheet.js";
import { McpCredentialRow, useMcpCredential } from "../pages/Connectors/McpCredentialRow.js";
import { McpServerForm } from "../pages/Connectors/McpServerForm.js";
import { McpToolList } from "../pages/Connectors/McpToolList.js";
import type { McpRegisterResponse, McpServer, McpServerSummary, McpServerTool } from "../pages/Connectors/types.js";
import type { ConnectionsTabProps } from "./ConnectionsPage.js";

type LoadState =
  | { kind: "loading" }
  | { kind: "loaded"; servers: McpServerSummary[] }
  | { kind: "error"; error: string };

export interface McpDetail {
  server: McpServer;
  tools: McpServerTool[];
  refreshing: boolean;
  error: string | null;
}

const MARK_STYLE = { "--c": "var(--agent-finance)" } as CSSProperties;

export function mcpStateLine(server: McpServerSummary): string {
  const host = server.url.replace(/^https?:\/\//, "").replace(/\/.*$/, "");
  return `${host} · ${server.enabledToolCount} of ${server.toolCount} tools on`;
}

/** One registered server in the sheet. */
export function McpSheet({
  summary,
  detail,
  onRefresh,
  onDelete,
  onToolUpdated,
  onToolsReplaced,
  onClose,
}: {
  summary: McpServerSummary;
  detail: McpDetail | null;
  onRefresh: () => void;
  onDelete: () => void;
  onToolUpdated: (tool: McpServerTool) => void;
  onToolsReplaced: (tools: McpServerTool[]) => void;
  onClose: () => void;
}) {
  const credential = useMcpCredential(summary.id);
  const bound = credential.status.kind === "bound";
  const server = detail?.server ?? summary;
  return (
    <>
      <RecordHead
        recordKey="MCP"
        title={server.name}
        onClose={onClose}
        more={
          <>
            <MenuItem onSelect={() => void navigator.clipboard?.writeText(server.url).catch(() => undefined)}>
              Copy server URL
            </MenuItem>
            <MenuItem onSelect={onDelete}>Delete this server</MenuItem>
          </>
        }
        meta={
          <span className="fact" style={{ fontSize: 11.5 }}>
            {server.url} · {summary.toolCount} tools
            {server.toolManifestFetchedAt ? ` · refreshed ${new Date(server.toolManifestFetchedAt).toLocaleString()}` : ""}
          </span>
        }
      />
      <div className="sbody" data-testid="mcp-sheet">
        {detail?.refreshing && <p className="body-p">Refreshing the manifest.</p>}
        {detail?.error && (
          <p role="alert" className="err-line">
            {detail.error}
          </p>
        )}
        <McpCredentialRow serverId={summary.id} credential={credential} />
        <div className="blk">
          <div className="bh">
            <span className="label">Tools</span>
            <span className="fact">risk · override · agent access · enabled</span>
          </div>
          {detail ? (
            <McpToolList
              server={detail.server}
              tools={detail.tools}
              onToolUpdated={onToolUpdated}
              onToolsReplaced={onToolsReplaced}
            />
          ) : (
            <p className="body-p">Reading the tools.</p>
          )}
        </div>
      </div>
      <SheetFoot
        why={
          <>
            Refreshing the manifest re-reads the tool list. <b>Overrides you set are kept.</b>
          </>
        }
        secondary={bound ? { label: "Disconnect credential", onClick: () => void credential.disconnect() } : undefined}
        primary={{ label: "Refresh manifest", onClick: onRefresh, disabled: Boolean(detail?.refreshing) }}
      />
    </>
  );
}

const NEW_MCP_FORM = "new-mcp-server-form";

function submitForm(id: string) {
  const form = document.getElementById(id);
  if (!(form instanceof HTMLFormElement)) return;
  if (typeof form.requestSubmit === "function") form.requestSubmit();
  else form.dispatchEvent(new Event("submit", { cancelable: true, bubbles: true }));
}

/** Register a server: name, URL, auth scheme and token; the tools arrive off unless select-all is ticked. */
export function NewMcpSheet({ onCreated, onCancel }: { onCreated: (input: McpRegisterResponse) => void; onCancel: () => void }) {
  const [submitting, setSubmitting] = useState(false);
  return (
    <>
      <RecordHead
        recordKey="NEW MCP"
        title="Add MCP server"
        onClose={onCancel}
        noExpand
        meta={
          <span className="fact" style={{ fontSize: 11.5 }}>
            Registered servers expose tools to the Teams, each with a risk level you can override.
          </span>
        }
      />
      <div className="sbody">
        <McpServerForm formId={NEW_MCP_FORM} bare onCreated={onCreated} onCancel={onCancel} onSubmittingChange={setSubmitting} />
      </div>
      <SheetFoot
        why={
          <>
            Registering reads the manifest and lists the tools with their default risk. <b>Nothing is enabled until you say so.</b>
          </>
        }
        secondary={{ label: "Cancel", onClick: onCancel }}
        primary={{ label: "Register MCP server", onClick: () => submitForm(NEW_MCP_FORM), disabled: submitting }}
      />
    </>
  );
}

export function McpTab({ refreshKey }: ConnectionsTabProps) {
  const [load, setLoad] = useState<LoadState>({ kind: "loading" });
  const [openId, setOpenId] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [detail, setDetail] = useState<McpDetail | null>(null);

  const reload = useCallback(async () => {
    try {
      const res = await fetch("/api/mcp-servers", { credentials: "include" });
      if (!res.ok) throw new Error(`Load failed (${res.status})`);
      const body = (await res.json()) as { servers: McpServerSummary[] };
      setLoad({ kind: "loaded", servers: body.servers });
    } catch (err) {
      setLoad({ kind: "error", error: err instanceof Error ? err.message : "Failed to load" });
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload, refreshKey]);

  const loadServer = useCallback(async (server: McpServerSummary) => {
    setDetail((prev) => ({ server, tools: prev?.server.id === server.id ? prev.tools : [], refreshing: false, error: null }));
    try {
      const res = await fetch(`/api/mcp-servers/${server.id}`, { credentials: "include" });
      if (!res.ok) throw new Error(`Load failed (${res.status})`);
      const body = (await res.json()) as { server: McpServer; tools: McpServerTool[] };
      setDetail((prev) => (prev && prev.server.id !== server.id ? prev : { server: body.server, tools: body.tools, refreshing: false, error: null }));
    } catch (err) {
      setDetail((prev) =>
        prev && prev.server.id !== server.id
          ? prev
          : { server, tools: prev?.tools ?? [], refreshing: false, error: err instanceof Error ? err.message : "Failed to load" },
      );
    }
  }, []);

  // A detail the registration already handed us is not read again.
  const skipRead = useRef<string | null>(null);
  const servers = load.kind === "loaded" ? load.servers : [];
  const open = openId ? (servers.find((server) => server.id === openId) ?? null) : null;
  useEffect(() => {
    if (!open) return;
    if (skipRead.current === open.id) {
      skipRead.current = null;
      return;
    }
    void loadServer(open);
    // Reads once per opened server: the summary row refreshing does not re-read it.
  }, [open?.id, loadServer]);

  async function refreshManifest(serverId: string) {
    setDetail((prev) => (prev ? { ...prev, refreshing: true, error: null } : prev));
    try {
      const res = await fetch(`/api/mcp-servers/${serverId}/refresh-manifest`, { method: "POST", credentials: "include" });
      if (!res.ok) {
        const j = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(j.error ?? `Refresh failed (${res.status})`);
      }
      const body = (await res.json()) as { server: McpServer; tools: McpServerTool[]; removed: string[] };
      setDetail({
        server: body.server,
        tools: body.tools,
        refreshing: false,
        error: body.removed.length > 0 ? `Removed upstream: ${body.removed.join(", ")}` : null,
      });
      void reload();
    } catch (err) {
      setDetail((prev) => (prev ? { ...prev, refreshing: false, error: err instanceof Error ? err.message : "Refresh failed" } : prev));
    }
  }

  async function deleteServer(serverId: string) {
    if (!window.confirm("Delete this MCP server? Tool history is preserved; agents will lose access.")) return;
    try {
      const res = await fetch(`/api/mcp-servers/${serverId}`, { method: "DELETE", credentials: "include" });
      if (!res.ok && res.status !== 204) throw new Error(`Delete failed (${res.status})`);
      setOpenId(null);
      void reload();
    } catch (err) {
      setDetail((prev) =>
        prev && prev.server.id === serverId ? { ...prev, error: err instanceof Error ? err.message : "Delete failed" } : prev,
      );
    }
  }

  function handleCreated(input: McpRegisterResponse) {
    setAdding(false);
    skipRead.current = input.server.id;
    // A warning means select-all was declined (the tool cap): say so where the tools are.
    setDetail({ server: input.server, tools: input.tools, refreshing: false, error: input.warning ?? null });
    setOpenId(input.server.id);
    void reload();
  }

  const sheet = adding ? (
    <NewMcpSheet onCreated={handleCreated} onCancel={() => setAdding(false)} />
  ) : open ? (
    <McpSheet
      key={open.id}
      summary={open}
      detail={detail && detail.server.id === open.id ? detail : null}
      onRefresh={() => void refreshManifest(open.id)}
      onDelete={() => void deleteServer(open.id)}
      onToolUpdated={(tool) =>
        setDetail((prev) => (prev ? { ...prev, tools: prev.tools.map((t) => (t.id === tool.id ? tool : t)) } : prev))
      }
      onToolsReplaced={(tools) => {
        // Bulk writes return the whole list; the summary's "n of m on" is the server's to count.
        setDetail((prev) => (prev ? { ...prev, tools } : prev));
        void reload();
      }}
      onClose={() => setOpenId(null)}
    />
  ) : null;
  useOwnedSheet(sheet, () => {
    setAdding(false);
    setOpenId(null);
  });

  return (
    <Groups>
      {load.kind === "error" && (
        <div className="qnote err" role="alert">
          <span>{load.error}</span>
        </div>
      )}
      {load.kind === "loaded" && servers.length === 0 ? (
        <div className="empty dashed tight" data-testid="mcp-empty-state">
          <p>No MCP servers registered. Connect a customer-hosted MCP server to expose its tools to your agents.</p>
        </div>
      ) : (
        <Group label="Registered" plain empty={load.kind === "loading" ? "Reading the servers." : undefined}>
          {servers.map((server) => (
            <Row
              key={server.id}
              className={`mcp-row-${server.slug}`}
              glyph={
                <span className="cmark" style={MARK_STYLE} aria-hidden>
                  M
                </span>
              }
              rowKey="MCP"
              title={server.name}
              tag={
                server.enabled
                  ? { label: "connected", color: "var(--accent-active)" }
                  : { label: "off", color: "var(--text-quiet)" }
              }
              state={mcpStateLine(server)}
              verb="Open"
              sel={!adding && server.id === openId}
              onOpen={() => {
                setAdding(false);
                setOpenId(server.id);
              }}
            />
          ))}
        </Group>
      )}
      <div className="qline">
        <span />
        <span className="acts">
          <Btn
            variant="secondary"
            size="sm"
            data-testid="mcp-add-button"
            onClick={() => {
              setOpenId(null);
              setAdding(true);
            }}
          >
            Add MCP server
          </Btn>
        </span>
      </div>
    </Groups>
  );
}
